import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  CalendarIcon,
  DocumentTextIcon,
  ChartBarIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const VipDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditingRewardRate, setIsEditingRewardRate] = useState(false);
  const [newRewardRate, setNewRewardRate] = useState('');
  const [isEditingFeeRate, setIsEditingFeeRate] = useState(false);
  const [newFeeRate, setNewFeeRate] = useState('');
  const [globalFeeRate, setGlobalFeeRate] = useState(0);

  const { data: vipData, isLoading } = useQuery(
    ['vip-detail', id],
    () => api.get(`/admin/vip/customers/${id}`).then(res => res.data)
  );

  const { data: rewardHistory } = useQuery(
    ['vip-rewards', id],
    () => api.get(`/admin/vip/customers/${id}/rewards`).then(res => res.data),
    { enabled: !!id }
  );

  const { data: withdrawalHistory } = useQuery(
    ['vip-withdrawals', id],
    () => api.get(`/admin/vip/customers/${id}/withdrawals`).then(res => res.data),
    { enabled: !!id }
  );

  const updateRewardRateMutation = useMutation(
    (rewardRate) => api.patch(`/admin/vip/customers/${id}/reward-rate`, { reward_rate: rewardRate }),
    {
      onSuccess: () => {
        toast.success('리워드 비율이 변경되었습니다.');
        queryClient.invalidateQueries(['vip-detail', id]);
        setIsEditingRewardRate(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '리워드 비율 변경에 실패했습니다.');
      }
    }
  );

  const updateFeeRateMutation = useMutation(
    (customFeeRate) => api.put(`/vip/${id}/fee-rate`, { customFeeRate }),
    {
      onSuccess: () => {
        toast.success('수수료율이 변경되었습니다.');
        queryClient.invalidateQueries(['vip-detail', id]);
        setIsEditingFeeRate(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '수수료율 변경에 실패했습니다.');
      }
    }
  );

  useEffect(() => {
    if (vipData) {
      setNewRewardRate(vipData.reward_rate);
      // custom_fee_rate가 null이나 undefined면 빈 문자열로 설정
      setNewFeeRate(vipData.custom_fee_rate !== null && vipData.custom_fee_rate !== undefined 
        ? vipData.custom_fee_rate.toString() 
        : '');
    }
  }, [vipData]);

  // 전역 수수료 설정 가져오기
  useEffect(() => {
    api.get('/vip/fee-settings')
      .then(res => {
        console.log('Global fee settings response:', res.data);
        setGlobalFeeRate(res.data?.defaultFeeRate || 0);
      })
      .catch(err => {
        console.error('Failed to fetch global fee rate:', err);
        // 오류 발생 시 기본값 3.3% 설정
        setGlobalFeeRate(3.3);
      });
  }, []);

  const handleRewardRateSubmit = () => {
    const rate = parseFloat(newRewardRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error('리워드 비율은 0~100 사이의 숫자여야 합니다.');
      return;
    }
    updateRewardRateMutation.mutate(rate);
  };

  const handleFeeRateSubmit = () => {
    if (newFeeRate === '') {
      // 전역 설정 사용 (null)
      updateFeeRateMutation.mutate(null);
      return;
    }
    
    const rate = parseFloat(newFeeRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error('수수료율은 0~100 사이의 숫자여야 합니다.');
      return;
    }
    updateFeeRateMutation.mutate(rate);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!vipData) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <p className="text-gray-500">VIP 고객 정보를 찾을 수 없습니다.</p>
          <button
            onClick={() => navigate('/admin/vip')}
            className="mt-4 text-indigo-600 hover:text-indigo-900"
          >
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 헤더 */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/vip')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          VIP 관리로 돌아가기
        </button>
        <h1 className="text-2xl font-bold text-gray-900">VIP 고객 상세정보</h1>
      </div>

      {/* 기본 정보 카드 */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">고객 정보</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">이름</dt>
                <dd className="text-sm text-gray-900">{vipData.User?.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">연락처</dt>
                <dd className="text-sm text-gray-900">{vipData.User?.phone}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">이메일</dt>
                <dd className="text-sm text-gray-900">{vipData.User?.email || '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">VIP 등록일</dt>
                <dd className="text-sm text-gray-900">
                  {new Date(vipData.created_at).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>
          <div>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">리워드 비율</dt>
                <dd className="flex items-center space-x-2">
                  {isEditingRewardRate ? (
                    <>
                      <input
                        type="number"
                        value={newRewardRate}
                        onChange={(e) => setNewRewardRate(e.target.value)}
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        step="0.1"
                        min="0"
                        max="100"
                      />
                      <span className="text-sm text-gray-900">%</span>
                      <button
                        onClick={handleRewardRateSubmit}
                        className="px-2 py-1 text-xs text-white bg-indigo-600 rounded hover:bg-indigo-700"
                      >
                        저장
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingRewardRate(false);
                          setNewRewardRate(vipData.reward_rate);
                        }}
                        className="px-2 py-1 text-xs text-gray-600 bg-gray-200 rounded hover:bg-gray-300"
                      >
                        취소
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-gray-900">{vipData.reward_rate}%</span>
                      <button
                        onClick={() => setIsEditingRewardRate(true)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  개별 수수료율
                  <span className="text-xs text-gray-400 ml-1">(전역: {globalFeeRate}%)</span>
                </dt>
                <dd className="flex items-center space-x-2">
                  {isEditingFeeRate ? (
                    <>
                      <input
                        type="number"
                        value={newFeeRate}
                        onChange={(e) => setNewFeeRate(e.target.value)}
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        step="0.1"
                        min="0"
                        max="100"
                        placeholder={globalFeeRate.toString()}
                      />
                      <span className="text-sm text-gray-900">%</span>
                      <button
                        onClick={handleFeeRateSubmit}
                        className="px-2 py-1 text-xs text-white bg-indigo-600 rounded hover:bg-indigo-700"
                      >
                        저장
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingFeeRate(false);
                          setNewFeeRate(vipData.custom_fee_rate !== null && vipData.custom_fee_rate !== undefined 
                            ? vipData.custom_fee_rate.toString() 
                            : '');
                        }}
                        className="px-2 py-1 text-xs text-gray-600 bg-gray-200 rounded hover:bg-gray-300"
                      >
                        취소
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm text-gray-900">
                        {vipData.custom_fee_rate !== null && vipData.custom_fee_rate !== undefined 
                          ? `${vipData.custom_fee_rate}% (개별 설정)` 
                          : `${globalFeeRate}% (전역 설정)`}
                      </span>
                      <button
                        onClick={() => setIsEditingFeeRate(true)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">총 적립액</dt>
                <dd className="text-sm font-semibold text-green-600">
                  {vipData.total_earned?.toLocaleString()}원
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">총 인출액</dt>
                <dd className="text-sm font-semibold text-red-600">
                  {vipData.total_withdrawn?.toLocaleString()}원
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">현재 잔액</dt>
                <dd className="text-lg font-bold text-indigo-600">
                  {vipData.current_balance?.toLocaleString()}원
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* 계좌 정보 카드 */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">계좌 정보</h2>
        {vipData.bank_name ? (
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">은행명</dt>
              <dd className="text-sm text-gray-900">{vipData.bank_name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">계좌번호</dt>
              <dd className="text-sm text-gray-900">{vipData.bank_account}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">예금주</dt>
              <dd className="text-sm text-gray-900">{vipData.account_holder}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-gray-500">등록된 계좌 정보가 없습니다.</p>
        )}
      </div>

      {/* 리워드 내역 */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">리워드 적립 내역</h2>
        {rewardHistory && rewardHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    날짜
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    프로젝트
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    계약금액
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    적립금액
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    잔액
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rewardHistory.map((reward) => (
                  <tr key={reward.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(reward.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {reward.project?.title || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {reward.project?.contract_amount?.toLocaleString()}원
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      +{reward.amount?.toLocaleString()}원
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {reward.balance_after?.toLocaleString()}원
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">아직 적립 내역이 없습니다.</p>
        )}
      </div>

      {/* 인출 내역 */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">인출 내역</h2>
        {withdrawalHistory && withdrawalHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    요청일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    인출금액
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    수수료
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    실지급액
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    처리일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    메모
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {withdrawalHistory.map((withdrawal) => (
                  <tr key={withdrawal.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(withdrawal.requested_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                      -{withdrawal.amount?.toLocaleString()}원
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                      {withdrawal.fee_amount ? `-${withdrawal.fee_amount?.toLocaleString()}원 (${withdrawal.fee_rate}%)` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      {withdrawal.net_amount ? `${withdrawal.net_amount?.toLocaleString()}원` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        withdrawal.status === 'completed' ? 'bg-green-100 text-green-800' :
                        withdrawal.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        withdrawal.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {withdrawal.status === 'completed' ? '완료' :
                         withdrawal.status === 'rejected' ? '거절' :
                         withdrawal.status === 'approved' ? '승인' : '대기'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {withdrawal.processed_at ? new Date(withdrawal.processed_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {withdrawal.memo || withdrawal.reject_reason || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500">아직 인출 내역이 없습니다.</p>
        )}
      </div>
    </div>
  );
};

export default VipDetail;